import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { ContentCard } from '../components/ContentCard';
import { IdCardIcon, VolumeXIcon, SearchIcon, MessageCircleOffIcon, HelpCircleIcon, XCircleIcon, AlertTriangleIcon } from 'lucide-react';
const tips = [{
  icon: IdCardIcon,
  title: 'Provide Required Documents',
  description: 'You MUST provide your license, registration, and proof of insurance when asked. This is required by Michigan law.',
  variant: 'important' as const
}, {
  icon: VolumeXIcon,
  title: 'Right to Remain Silent',
  description: "Beyond identification, you have the constitutional right to remain silent. You don't have to answer questions about where you're going or where you've been.",
  variant: 'default' as const
}, {
  icon: SearchIcon,
  title: "Don't Consent to Searches",
  description: 'Politely decline any search requests. Say: "I do not consent to searches." Officers need a warrant or probable cause to search without consent.',
  variant: 'warning' as const
}, {
  icon: MessageCircleOffIcon,
  title: 'Decline Casual Questions',
  description: '"Where are you headed?" and "Where are you coming from?" are fishing questions. Politely say: "I prefer not to answer that."',
  variant: 'default' as const
}, {
  icon: HelpCircleIcon,
  title: "Ask If You're Free to Go",
  description: 'If the stop seems prolonged without clear reason, calmly ask: "Am I being detained, or am I free to go?" This establishes your rights.',
  variant: 'tip' as const
}, {
  icon: XCircleIcon,
  title: 'Never Admit Fault',
  description: 'Don\'t apologize or admit to anything. Even saying "sorry" can be used against you. Stay polite but neutral.',
  variant: 'warning' as const
}];
const container = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0
  }
};
export function WhatToSayToCops() {
  return <div className="min-h-screen bg-[#0a0a0a] safe-bottom" data-id="element-339">
      <PageHeader title="What to Say to Cops" subtitle="Know your rights during traffic stops" data-id="element-340" />

      <main className="px-4 py-6" data-id="element-341">
        {/* Warning Banner */}
        <motion.div className="bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-2xl p-4 mb-6 flex gap-3" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} data-id="element-342">
          <AlertTriangleIcon className="w-6 h-6 text-[#dc2626] flex-shrink-0 mt-0.5" data-id="element-343" />
          <div data-id="element-344">
            <p className="text-[#dc2626] font-semibold mb-1" data-id="element-345">
              Stay Calm & Respectful
            </p>
            <p className="text-[#a3a3a3] text-sm" data-id="element-346">
              Being polite protects you legally. Aggressive behavior can
              escalate situations and hurt your case.
            </p>
          </div>
        </motion.div>

        <motion.div className="space-y-3" variants={container} initial="hidden" animate="show" data-id="element-347">
          {tips.map((tip, index) => <motion.div key={index} variants={item} data-id="element-348">
              <ContentCard icon={tip.icon} title={tip.title} description={tip.description} variant={tip.variant} data-id="element-349" />
            </motion.div>)}
        </motion.div>

        {/* Quick Reference */}
        <motion.div className="mt-6 bg-[#d4a853]/10 border border-[#d4a853]/30 rounded-2xl p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} data-id="element-350">
          <p className="text-[#d4a853] font-semibold mb-2" data-id="element-351">
            Quick Phrases to Remember:
          </p>
          <ul className="space-y-2 text-sm text-[#a3a3a3]" data-id="element-352">
            <li data-id="element-353">"I do not consent to searches."</li>
            <li data-id="element-354">"I prefer not to answer that question."</li>
            <li data-id="element-355">"Am I being detained, or am I free to go?"</li>
            <li data-id="element-356">"I would like to speak with my attorney."</li>
          </ul>
        </motion.div>
      </main>
    </div>;
}